import * as THREE from 'three';

import type { BannerDesign, BannerSpec } from '../../network/Banners';

/**
 * Płat sztandaru: obrazek gracza na falującej płachcie.
 *
 * **Piksele zostają pikselami.** Tekstura ma dokładnie tyle pól, ile projekt,
 * i filtr najbliższego sąsiada — sztandar jest rysunkiem z pól, a rozmyty
 * wyglądałby jak zdjęcie rysunku. Pola spoza kształtu są przezroczyste, więc
 * trójkąt i języki wycina maska, a nie geometria.
 *
 * **Falowanie jest w shaderze**, jedną wspólną chwilą dla wszystkich płatów
 * (`bannerClock`): przy drzewcu płat stoi, a im dalej od niego, tym mocniej się
 * wygina.
 */

/** Zegar falowania — przesuwany raz na klatkę przez grę. */
export const bannerClock = { value: 0 };

/** Obrazek projektu jako tekstura. */
export function bannerTexture(design: BannerDesign, spec: BannerSpec): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = spec.width;
  canvas.height = spec.height;
  paintBanner(canvas, design, spec);

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = false;
  return texture;
}

/**
 * Narysuj projekt na płótnie, jedno pole na piksel. Wspólne dla gry i edytora,
 * więc sztandar wygląda tak samo tu i tam.
 */
export function paintBanner(canvas: HTMLCanvasElement, design: BannerDesign, spec: BannerSpec): void {
  const context = canvas.getContext('2d');
  if (!context) return;
  const mask = spec.shapes.find((shape) => shape.key === design.shape)?.mask;
  const image = context.createImageData(spec.width, spec.height);
  const rgb = spec.palette.map((hex) => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ]);

  for (let i = 0; i < spec.width * spec.height; i++) {
    const inside = mask ? mask[i] === 1 : true;
    const [r, g, b] = rgb[design.pixels[i] ?? 0] ?? [0, 0, 0];
    image.data.set([r, g, b, inside ? 255 : 0], i * 4);
  }
  context.putImageData(image, 0, 0);
}

/**
 * Płachta o podanej wysokości, przybita lewym brzegiem do punktu (0, 0, 0)
 * i zwisająca w dół; powiewa wzdłuż +X.
 */
export function bannerCloth(texture: THREE.Texture, height: number, spec: BannerSpec): THREE.Mesh {
  const width = (height * spec.width) / spec.height;
  const geometry = new THREE.PlaneGeometry(width, height, 12, 1);
  geometry.translate(width / 2, -height / 2, 0);

  const material = new THREE.MeshLambertMaterial({
    map: texture,
    side: THREE.DoubleSide,
    alphaTest: 0.5,
  });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.bannerTime = bannerClock;
    shader.uniforms.bannerWidth = { value: width };
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float bannerTime;\nuniform float bannerWidth;'
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        float bannerReach = clamp(position.x / bannerWidth, 0.0, 1.0);
        transformed.z += sin(bannerTime * 3.2 - position.x * 9.0) * 0.06 * bannerWidth * bannerReach;
        transformed.y += sin(bannerTime * 2.1 - position.x * 5.0) * 0.02 * bannerWidth * bannerReach;`
      );
  };

  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  // Cień z tą samą maską, inaczej trójkąt rzucałby cień prostokąta. Bez
  // falowania — cień płata nie jest rzeczą, której różnicę widać.
  mesh.customDepthMaterial = new THREE.MeshDepthMaterial({
    depthPacking: THREE.RGBADepthPacking,
    map: texture,
    alphaTest: 0.5,
  });
  return mesh;
}
